import {
  Box,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@material-ui/core";
import MyTypography from "@saleor/components/MyTypography";
import StickyHorizontalScrollBar from "@saleor/components/StickyHorizontalScrollBar";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePaginationCustom from "@saleor/components/TablePaginationCustom";
import { FailedFilesQuery } from "@saleor/graphql";
import { maybe, renderCollection } from "@saleor/misc";
import { ListProps, ListSettings, RelayToFlat, SortPage } from "@saleor/types";
import { formatDateTime } from "@saleor/utils/date";
import { getArrowDirection } from "@saleor/utils/sort";
import React, { FC, useRef } from "react";
import { useIntl } from "react-intl";

import { FailedFilesListSortField } from "../../urls";
import RetryButton from "../RetryButton";

const useStyles = makeStyles(
  theme => ({
    root: {
      borderRadius: "2px",
      overflowX: "auto",
      width: "100%",

      "&::-webkit-scrollbar": {
        height: "14px",
        background: "#FFFFFF"
      },
      "&::-webkit-scrollbar-track": {
        borderRadius: 10
      },
      "&::-webkit-scrollbar-thumb": {
        background: "#CCCCCC",
        borderRadius: 10,
        border: "4px solid #FFFFFF",
        height: "6px"
      }
    },
    tableWrapper: {
      "& .FailFileTable-root-158 .MuiTableCell-head *": {
        color: "#222222 !important"
      }
    },
    rowBody: {
      height: 64
    },
    colFailData: {
      width: 300,
      paddingLeft: 12
    },
    title: {
      margin: "40px 0 24px 0"
    },
    colFileName: {
      "& .MuiTypography-root": {
        color: theme.palette.grey[800]
      }
    },
    colRetry: {
      width: 200
    },
    modal: {
      maxWidth: 400
    },
    colDate: {
      "& .MuiTypography-root": {
        color: theme.palette.grey[900]
      }
    },
    paginateFooter: {
      background: "rgba(212, 244, 248, 0.8)",
      padding: "12px 20px",
      borderBottom: `1px solid ${theme.palette.grey[300]}`,
      borderRight: `1px solid ${theme.palette.grey[300]}`,
      borderLeft: `1px solid ${theme.palette.grey[300]}`
    }
  }),
  { name: "FailFileTable" }
);

interface POFailFileTableProps
  extends Omit<ListProps, "onRowClick">,
    SortPage<FailedFilesListSortField> {
  data?: RelayToFlat<FailedFilesQuery["failedFiles"]>;
  settings: ListSettings;
  onPreviousFirstPage: () => void;
  onNextLastPage: () => void;
  totalCount: number | null;
  latestPageItemNumber: number | null;
  onHandleOpenDialog: (id: string, fileName: string) => void;
}

const POFailFileTable: FC<POFailFileTableProps> = ({
  data,
  sort,
  onSort,
  settings,
  onNextLastPage,
  onPreviousFirstPage,
  totalCount,
  latestPageItemNumber,
  pageInfo,
  onPreviousPage,
  onNextPage,
  onUpdateListSettings,
  disabled,
  onHandleOpenDialog
}) => {
  const intl = useIntl();
  const classes = useStyles();
  const containerRef = useRef();

  return (
    <Box className={classes.tableWrapper}>
      <MyTypography className={classes.title} variant="h7">
        {intl.formatMessage({ defaultMessage: "Manage Fail File" })}
      </MyTypography>
      <div className={classes.root} ref={containerRef}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCellHeader
                className={classes.colFailData}
                direction={
                  sort.sort === FailedFilesListSortField.UPDATED_AT
                    ? getArrowDirection(sort.asc)
                    : undefined
                }
                arrowPosition="right"
                onClick={() => onSort(FailedFilesListSortField.UPDATED_AT)}
              >
                Fail Data/Time
              </TableCellHeader>
              <TableCellHeader>File Name</TableCellHeader>
              <TableCellHeader textAlign="center">Retry</TableCellHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {renderCollection(data, (item, index) => (
              <TableRow className={classes.rowBody} key={index}>
                <TableCell>
                  <MyTypography className={classes.colDate} variant="button3">
                    {maybe(() => formatDateTime(item.updatedAt))}
                  </MyTypography>
                </TableCell>
                <TableCell className={classes.colFileName}>
                  <MyTypography variant="body3">
                    {maybe(() => item.fileName)}
                  </MyTypography>
                </TableCell>
                <TableCell className={classes.colRetry}>
                  <RetryButton
                    onClick={() => {
                      onHandleOpenDialog(item.id, item.fileName);
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <StickyHorizontalScrollBar container={containerRef} />
      </div>
      <Box className={classes.paginateFooter}>
        <TablePaginationCustom
          disabled={disabled}
          settings={settings}
          hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
          onNextPage={onNextPage}
          onPreviousPage={onPreviousPage}
          onPreviousFirstPage={onPreviousFirstPage}
          onNextLastPage={onNextLastPage}
          onUpdateListSettings={onUpdateListSettings}
          hasPreviousPage={
            pageInfo && !disabled ? pageInfo.hasPreviousPage : false
          }
          latestPageItemNumber={latestPageItemNumber}
          totalCount={totalCount || 0}
          isBackFistPage
          isNextLastPage
          isShowIndex
        />
      </Box>
    </Box>
  );
};

export default POFailFileTable;
